import type { AttachQuotePayload } from '@/services/quotes/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Field,
  Select,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useQuoteParams } from '../../context/quote-params.context';
import { useQuoteSummary } from '../../context/quote-summary.context';
import { useAssistances } from '../../hooks/homeowner/use-assistances.hook';
import { useCoverages } from '../../hooks/homeowner/use-coverages.hook';
import { usePlanResourcesByCode } from '../../hooks/homeowner/use-plan-resources-by-code.hook';
import { withForm } from '../../hooks/common/use-quote-form.hook';
import { useUpdateBusinessQuote } from '../../hooks/business/use-update-quote.hook';
import { useUpdateQuote } from '../../hooks/homeowner/use-update-quote.hook';
import { AssistancePackageCard } from '../assistance-package-card.component';
import {
  CoverageCard,
  formatCustomDeductibleLabel,
  mapDeductibleOptions,
  type CoverageCardData,
  type CustomDeductible,
} from '../coverage-card.component';

const formOpts = formOptions({
  defaultValues: {},
});

export const StepCoverages = withForm({
  ...formOpts,
  props: {
    onNext: () => {},
    onBack: () => {},
    stepCode: '',
    vrdTotal: undefined as number | undefined,
    coverages: [] as CoverageCardData[],
    setCoverages: (() => undefined) as unknown as Dispatch<
      SetStateAction<CoverageCardData[]>
    >,
    assistanceChoice: '',
    setAssistanceChoice: (() => undefined) as unknown as Dispatch<
      SetStateAction<string>
    >,
    isViewMode: false,
  },
  render: ({
    onNext,
    onBack,
    stepCode,
    vrdTotal,
    coverages: persistedCoverages,
    setCoverages: setPersistedCoverages,
    assistanceChoice = '',
    setAssistanceChoice,
    isViewMode,
  }) => {
    const versionDate = '2025-11-03';
    const { data, isLoading, error, refetch } = useCoverages(versionDate);
    const { id, category } = useQuoteParams();
    const isBusinessQuote = category === 'BUSINESS_INSURANCE';
    const { mutateAsync: mutateHomeownerAsync, isPending: isHomeownerPending } =
      useUpdateQuote();
    const { mutateAsync: mutateBusinessAsync, isPending: isBusinessPending } =
      useUpdateBusinessQuote();
    const mutateAsync = isBusinessQuote
      ? mutateBusinessAsync
      : mutateHomeownerAsync;
    const isPending = isBusinessQuote ? isBusinessPending : isHomeownerPending;
    const coverages: CoverageCardData[] = persistedCoverages || [];
    const displayCoverages = useMemo(() => {
      const isHiddenCoverage = (cov: CoverageCardData) => {
        const title = (cov.title || '').toLowerCase();
        return title.includes('capitalização') || title.includes('capitalizacao');
      };

      const requiredCoverages = coverages.filter(
        cov => cov.required && !isHiddenCoverage(cov)
      );
      const additionalCoverages = coverages
        .filter(cov => !cov.required && !isHiddenCoverage(cov))
        .sort((a, b) =>
          (a.title || '').localeCompare(b.title || '', 'pt-BR', {
            sensitivity: 'base',
          })
        );

      return [...requiredCoverages, ...additionalCoverages];
    }, [coverages]);
    const { updateCoverages, updateAssistance } = useQuoteSummary();
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const coveragesRef = useRef(coverages);
    const getPlanLabel = () =>
      (assists || []).find(plan => plan.code === assistanceChoice)?.name || '';

    const parseCurrencyLikeToNumber = (value: string): number => {
      const numeric = parseInt(String(value || '').replace(/\D/g, ''), 10);
      if (Number.isNaN(numeric)) return 0;
      return numeric / 100;
    };

    const formatPackageDisplayName = (name: string) => {
      const n = (name || '').trim();
      if (!n) return '';
      return n.toLowerCase().startsWith('pacote ') ? n : `Pacote ${n}`;
    };
    const formatCurrency = (value: number) =>
      value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });

    const getAssistanceSummary = () => {
      const assistancesList: string[] = [];
      const rafflesList: string[] = [];

      if (Array.isArray(planResources)) {
        planResources.forEach(resource => {
          const code = String(resource.code || '')
            .trim()
            .toUpperCase();
          if (code === 'CAPITA') return;

          const name = resource.name?.trim();
          if (!name) return;
          const normalized = name.toLowerCase();
          if (
            normalized.includes('sorteio') ||
            normalized.includes('capitaliz')
          ) {
            rafflesList.push(name);
          } else {
            assistancesList.push(name);
          }
        });
      }

      return { assistancesList, rafflesList };
    };

    useEffect(() => {
      coveragesRef.current = coverages;
    });

    useEffect(() => {
      if (!data || !setPersistedCoverages) return;

      const currentCoverages = coveragesRef.current;

      const NON_COVERAGE_CODES = new Set(['ASSIST', 'CAPITA']);
      const catalog: CoverageCardData[] = data
        .filter(item => !NON_COVERAGE_CODES.has(String(item.code || '').trim()))
        .map(item => ({
          id: String(item.code || '').trim() || item.id,
          code: item.code,
          productElementId: item.productElementId,
          title: item.name,
          description: item.description || '',
          required: item.required,
          limit: {
            type: 'range',
            constraints: {
              min: item.limit?.constraints?.min ?? 0,
              max: item.limit?.constraints?.max ?? 999999999,
            },
          },
          limitValue: '',
          deductible: '',
          deductibleOptions: mapDeductibleOptions(item.deductibles ?? []),
          indemnityPeriod: item.required ? '' : undefined,
        }));

      if (!currentCoverages || currentCoverages.length === 0) {
        setPersistedCoverages(catalog);
        return;
      }

      const byCode = new Map<string, CoverageCardData>();
      currentCoverages.forEach(cov => {
        const code = String(cov.code || '').trim();
        if (!code) return;
        byCode.set(code, cov);
      });

      const merged = catalog.map(item => {
        const existing = byCode.get(String(item.code || '').trim());
        if (!existing) return item;

        let resolvedDeductible = existing.deductible ?? item.deductible;
        const opts = item.deductibleOptions ?? [];
        if (resolvedDeductible && opts.length > 0) {
          const isValidValue = opts.some(
            opt => opt.value === resolvedDeductible
          );
          if (!isValidValue) {
            const matchByCode = opts.find(
              opt => opt.code === resolvedDeductible
            );
            if (matchByCode) {
              resolvedDeductible = matchByCode.value;
            }
          }
        }

        return {
          ...item,
          limitValue: existing.limitValue ?? item.limitValue,
          deductible: resolvedDeductible,
          deductibleOptions: item.deductibleOptions,
          indemnityPeriod: existing.indemnityPeriod ?? item.indemnityPeriod,
          customDeductible: existing.customDeductible,
        };
      });

      setPersistedCoverages(merged);
    }, [data, setPersistedCoverages]);

    useEffect(() => {
      const filtered = coverages
        .filter(cov => {
          if (!cov.limitValue || cov.limitValue.toString().trim() === '')
            return false;
          const numeric = parseInt(
            String(cov.limitValue).replace(/\D/g, ''),
            10
          );
          return !Number.isNaN(numeric) && numeric > 0;
        })
        .map(cov => {
          const customLabel = cov.customDeductible
            ? formatCustomDeductibleLabel(cov.customDeductible)
            : '';
          const defaultLabel = (cov.deductibleOptions ?? []).find(
            opt => opt.value === cov.deductible
          )?.label;
          return {
            id: cov.id,
            title: cov.title,
            amountLabel: cov.limitValue,
            franchiseLabel: customLabel || defaultLabel,
          };
        });
      updateCoverages(filtered);
    }, [coverages, updateCoverages]);

    const handleLimitChange = (id: string, value: string) => {
      if (!setPersistedCoverages) return;
      if (fieldErrors[id]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[id];
          return newErrors;
        });
      }
      setPersistedCoverages((prev: CoverageCardData[]) =>
        prev.map((cov: CoverageCardData) =>
          cov.id === id ? { ...cov, limitValue: value } : cov
        )
      );
    };

    const handleDeductibleChange = (id: string, value: string) => {
      if (!setPersistedCoverages) return;
      if (fieldErrors[id]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[id];
          return newErrors;
        });
      }
      setPersistedCoverages((prev: CoverageCardData[]) =>
        prev.map((cov: CoverageCardData) =>
          cov.id === id ? { ...cov, deductible: value } : cov
        )
      );
    };

    const handleCustomDeductibleChange = (
      id: string,
      value: CustomDeductible | undefined
    ) => {
      if (!setPersistedCoverages) return;
      setPersistedCoverages((prev: CoverageCardData[]) =>
        prev.map((cov: CoverageCardData) =>
          cov.id === id ? { ...cov, customDeductible: value } : cov
        )
      );
    };

    const handleIndemnityPeriodChange = (id: string, value: string) => {
      if (!setPersistedCoverages) return;
      if (fieldErrors[id]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[id];
          return newErrors;
        });
      }
      setPersistedCoverages((prev: CoverageCardData[]) =>
        prev.map((cov: CoverageCardData) =>
          cov.id === id ? { ...cov, indemnityPeriod: value } : cov
        )
      );
    };

    const handleSubmit = async () => {
      try {
        const hasAssistanceSelected =
          typeof assistanceChoice === 'string' &&
          assistanceChoice.trim().length > 0;

        const hasPlanResources =
          Array.isArray(planResources) && planResources.length > 0;

        const requiredFieldErrors: Record<string, string> = {};
        coverages.forEach(cov => {
          if (!cov.required) return;
          const lmiAmount = parseCurrencyLikeToNumber(
            String(cov.limitValue || '')
          );
          const hasDeductibleOptions = (cov.deductibleOptions ?? []).length > 0;
          const hasDeductibleSelected =
            typeof cov.deductible === 'string' &&
            cov.deductible.trim().length > 0;

          if (lmiAmount <= 0) {
            requiredFieldErrors[cov.id] = 'Campo obrigatório';
            return;
          }

          if (hasDeductibleOptions && !hasDeductibleSelected) {
            requiredFieldErrors[cov.id] = 'Campo obrigatório';
          }
        });

        if (Object.keys(requiredFieldErrors).length > 0) {
          setShowValidationErrors(true);
          setFieldErrors(prev => ({ ...prev, ...requiredFieldErrors }));
          const missingTitles = coverages
            .filter(cov => requiredFieldErrors[cov.id])
            .map(cov => cov.title);
          toaster.create({
            type: 'error',
            title: 'Campos obrigatórios',
            description:
              missingTitles.length > 0
                ? `Preencha os campos obrigatórios das coberturas: ${missingTitles.join(', ')}.`
                : 'Preencha os campos obrigatórios para avançar.',
          });
          return;
        }

        const coverageConsistencyErrors: Record<string, string> = {};
        coverages.forEach(cov => {
          const hasDeductibleOptions = (cov.deductibleOptions ?? []).length > 0;
          if (!hasDeductibleOptions) return;

          const hasDeductibleSelected =
            typeof cov.deductible === 'string' &&
            cov.deductible.trim().length > 0;
          const lmiAmount = parseCurrencyLikeToNumber(
            String(cov.limitValue || '')
          );
          const hasLmiSelected = lmiAmount > 0;

          if (hasLmiSelected && !hasDeductibleSelected) {
            coverageConsistencyErrors[cov.id] =
              'Selecione a franquia para esta cobertura antes de avançar.';
            return;
          }

          if (!hasLmiSelected && hasDeductibleSelected) {
            coverageConsistencyErrors[cov.id] =
              'Informe um LMI maior que zero para a cobertura com franquia selecionada.';
          }
        });

        if (
          !hasAssistanceSelected ||
          !hasPlanResources ||
          Object.keys(coverageConsistencyErrors).length > 0
        ) {
          setShowValidationErrors(true);
          if (Object.keys(coverageConsistencyErrors).length > 0) {
            setFieldErrors(prev => ({ ...prev, ...coverageConsistencyErrors }));
            toaster.create({
              type: 'error',
              title: 'Coberturas sem franquia válida',
              description:
                'Corrija as coberturas selecionadas para continuar: toda cobertura com LMI deve ter franquia e toda franquia precisa de LMI maior que zero.',
            });
          } else if (!hasAssistanceSelected || !hasPlanResources) {
            toaster.create({
              type: 'error',
              title: 'Pacote de assistência não selecionado',
              description:
                'Selecione um pacote de assistência válido para avançar.',
            });
          }
          return;
        }

        // A cobertura básica (obrigatória) não pode ter LMI maior que o valor
        // em risco informado na etapa anterior. As coberturas adicionais são
        // validadas pelo backend no attach-element.
        const maxAllowed =
          typeof vrdTotal === 'number' && vrdTotal > 0 ? vrdTotal : undefined;
        if (maxAllowed !== undefined) {
          const vrdErrors: Record<string, string> = {};
          coverages.forEach(cov => {
            if (!cov.required) return;
            const amount = parseCurrencyLikeToNumber(
              String(cov.limitValue || '')
            );
            if (amount > 0 && amount > maxAllowed) {
              vrdErrors[cov.id] =
                `O Limite Máximo de Indenização da cobertura ${cov.title} não pode exceder o valor em risco informado de ${formatCurrency(maxAllowed)}.`;
            }
          });

          if (Object.keys(vrdErrors).length > 0) {
            setShowValidationErrors(true);
            setFieldErrors(prev => ({ ...prev, ...vrdErrors }));
            toaster.create({
              type: 'error',
              title: 'LMI excede o valor em risco',
              description:
                'O Limite Máximo de Indenização da cobertura básica não pode ser maior que o valor em risco informado na etapa anterior.',
            });
            return;
          }
        }

        const parsedCoverages = coverages
          .map(cov => {
            const value = parseCurrencyLikeToNumber(
              String(cov.limitValue || '')
            );

            const selectedDeductible = (cov.deductibleOptions ?? []).find(
              opt => opt.value === cov.deductible
            );

            return {
              code: cov.code || cov.id,
              value,
              ...(cov.productElementId
                ? { productElementId: cov.productElementId }
                : {}),
              ...(selectedDeductible
                ? {
                    codeDeductible: selectedDeductible.code,
                  }
                : {}),
            };
          })
          .filter(c => c.value > 0);

        // Benefícios de assistência (serviços com código de 8 dígitos)
        const parsedBenefits = Array.isArray(planResources)
          ? planResources
              .filter(resource => {
                const code = String(resource.code || '')
                  .trim()
                  .toUpperCase();

                if (code === 'CAPITA') return false;

                const name = resource.name?.trim();
                if (!name) return false;

                const normalized = name.toLowerCase();
                const isRaffle =
                  normalized.includes('sorteio') ||
                  normalized.includes('capitaliz');

                if (isRaffle) return false;

                if (code === '676063' || code === '676067') return false;

                if (!/^\d{8}$/.test(code)) return false;

                return true;
              })
              .map(resource => ({
                code: String(resource.code || '').trim(),
                value: resource.defaultValue ?? 0,
                ...(resource.productElementId
                  ? { productElementId: resource.productElementId }
                  : {}),
              }))
          : [];

        // ASSIST coverage com benefits aninhados
        const assistCoverage = {
          code: 'ASSIST',
          value: 0,
          planCode: assistanceChoice,
          benefits: parsedBenefits,
        };

        // CAPITA coverage com itens após ele (sorteios) como benefits
        const capitaIndex = Array.isArray(planResources)
          ? planResources.findIndex(
              r =>
                String(r.code || '')
                  .trim()
                  .toUpperCase() === 'CAPITA'
            )
          : -1;

        const capitaBenefits =
          capitaIndex >= 0 && Array.isArray(planResources)
            ? planResources.slice(capitaIndex + 1).map(r => ({
                code: String(r.code || '').trim(),
                value: r.defaultValue ?? 0,
                ...(r.productElementId
                  ? { productElementId: r.productElementId }
                  : {}),
              }))
            : [];

        const capitaCoverage =
          capitaIndex >= 0
            ? {
                code: 'CAPITA',
                value: 0,
                planCode: assistanceChoice,
                ...(capitaBenefits.length > 0
                  ? { benefits: capitaBenefits }
                  : {}),
              }
            : null;

        const allCoverages = [
          ...parsedCoverages,
          assistCoverage,
          ...(capitaCoverage ? [capitaCoverage] : []),
        ];

        const payload: Partial<AttachQuotePayload> = {
          step: stepCode || '',
          coverages: allCoverages,
          ...(parsedBenefits.length > 0 ? { benefits: parsedBenefits } : {}),
        };

        if (!id) return;
        const result = await mutateAsync({ id, payload });
        if (result.success) {
          setShowValidationErrors(false);
          setFieldErrors({});

          const { assistancesList, rafflesList } = getAssistanceSummary();

          updateAssistance({
            packageName: formatPackageDisplayName(getPlanLabel()),
            assistances: assistancesList,
            raffles: rafflesList,
          });

          onNext?.();
        }
      } catch (error) {
        if (error instanceof ValidationError && error.fieldErrors) {
          const errors: Record<string, string> = {};
          const errorCoverageNames: string[] = [];

          const codeToName = new Map<string, string>();
          coverages.forEach(cov => {
            if (cov.code) codeToName.set(String(cov.code).trim(), cov.title);
          });

          const formatEquivalentCoverageMessage = (message: string): string => {
            const patterns = [
              /cobertura\s+(\d+).*?cobertura\s+(\d+)/i,
              /(\d{5})\s+.*?\s+(\d{5})/,
              /(exclusiv|proibid|conflict|equivalent)/i
            ];

            for (const pattern of patterns) {
              const match = message.match(pattern);
              if (match && match[1] && match[2]) {
                const firstCode = match[1];
                const secondCode = match[2];
                const firstCoverage = coverages.find(c => c.code === firstCode);
                const secondCoverage = coverages.find(c => c.code === secondCode);

                if (firstCoverage && secondCoverage) {
                  return `É proibida a contratação da cobertura ${secondCoverage.title} ao contratar a cobertura ${firstCoverage.title}`;
                }
              }
            }

            let result = message;
            codeToName.forEach((name, code) => {
              result = result.replace(new RegExp(`\\b${code}\\b`, 'g'), name);
            });
            return result;
          };

          Object.entries(error.fieldErrors).forEach(([key, value]) => {
            const enrichedMessage = formatEquivalentCoverageMessage(value.message);

            const codeMatch = value.message.match(/cobertura\s+(\w+)/i);
            const coverageCode = codeMatch ? codeMatch[1] : null;

            if (coverageCode) {
              const coverage = coverages.find(c => c.code === coverageCode);
              if (coverage) {
                errors[coverage.id] = enrichedMessage;
                if (!errorCoverageNames.includes(coverage.title)) {
                  errorCoverageNames.push(coverage.title);
                }
                return;
              }
            }

            const coverage = coverages.find(
              c => c.code === key || c.id === key
            );
            if (coverage) {
              errors[coverage.id] = enrichedMessage;
              if (!errorCoverageNames.includes(coverage.title)) {
                errorCoverageNames.push(coverage.title);
              }
            }
          });

          setFieldErrors(errors);

          toaster.create({
            type: 'error',
            title: 'Erro de validação',
            description:
              errorCoverageNames.length > 0
                ? `Erro nas coberturas: ${errorCoverageNames.join(', ')}`
                : error.messages.length > 0
                  ? error.messages.join('\n')
                  : 'Erros encontrados na validação.',
          });
        }
      }
    };
    const {
      data: assists,
      isLoading: isLoadingAssists,
      error: assistsError,
      refetch: refetchAssists,
    } = useAssistances();

    const {
      data: planResources,
      isLoading: isLoadingPlanResources,
      error: planResourcesError,
      refetch: refetchPlanResources,
    } = usePlanResourcesByCode(assistanceChoice);

    if (isLoading) {
      return (
        <div className='flex justify-center items-center p-12'>
          <Text>Carregando coberturas...</Text>
        </div>
      );
    }

    if (error) {
      return (
        <div className='flex flex-col items-center justify-center p-12 gap-4'>
          <Text className='text-red-500'>
            Erro ao carregar coberturas: {(error as Error).message}
          </Text>
          <Button variant='secondary' onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (!coverages || coverages.length === 0) {
      return (
        <div className='flex justify-center items-center p-12'>
          <Text>Nenhuma cobertura disponível</Text>
        </div>
      );
    }

    return (
      <div className='flex flex-col items-start p-0 gap-8 self-stretch'>
        <div className='flex flex-col items-start gap-8 self-stretch px-2 sm:px-6'>
          <div className='flex flex-col items-start p-0 gap-6 self-stretch'>
            <div className='flex flex-col items-start p-0 gap-2'>
              <Text className='text-lg font-bold text-alba-black-500'>
                Coberturas
              </Text>
            </div>

            <div className='flex flex-col items-start p-0 gap-4 self-stretch'>
              {displayCoverages.map(coverage => (
                <CoverageCard
                  key={String(coverage.code || '').trim() || coverage.id}
                  coverage={{
                    ...coverage,
                    onLimitChange: value =>
                      handleLimitChange(coverage.id, value),
                    onDeductibleChange: value =>
                      handleDeductibleChange(coverage.id, value),
                    onIndemnityPeriodChange: value =>
                      handleIndemnityPeriodChange(coverage.id, value),
                    onCustomDeductibleChange: value =>
                      handleCustomDeductibleChange(coverage.id, value),
                    showValidationErrors,
                    disabled: isViewMode,
                    validationError: fieldErrors[coverage.id],
                  }}
                />
              ))}
            </div>
          </div>

          <div className='flex flex-col items-start p-0 gap-6 self-stretch'>
            <RequiredFieldTooltip
              active
              className='flex w-full flex-col items-start gap-6'
            >
              <Text className='text-lg font-bold text-alba-black-500'>
                Assistências
                <span className='text-alba-error-red-500'>*</span>
              </Text>

            <div className='flex flex-col items-start p-0 gap-2 self-stretch'>
              <Field.Root className='w-1/2'>
                <Field.Label className='sr-only'>
                  Assistências <span className='text-red-500'>*</span>
                </Field.Label>
                <Select.Root
                  value={assistanceChoice ? [assistanceChoice] : []}
                  onValueChange={details => {
                    const value = details.value[0] || '';
                    setAssistanceChoice(value);
                    if (showValidationErrors) setShowValidationErrors(false);
                  }}
                  positioning={{ sameWidth: true }}
                  deselectable
                  items={(assists || []).map(plan => ({
                    value: plan.code,
                    label: plan.name,
                  }))}
                  disabled={isViewMode || isLoadingAssists}
                >
                  <Select.Control>
                    <Select.Trigger className='h-10 px-5 border border-alba-gray-400 rounded-xl'>
                      <Select.ValueText
                        placeholder={
                          isLoadingAssists
                            ? 'Carregando...'
                            : assistsError
                              ? 'Erro ao carregar'
                              : 'Selecione'
                        }
                        className='text-base text-alba-gray-600'
                      />
                      <Select.Indicator />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {(assists || []).map(plan => (
                        <Select.Item
                          key={plan.code}
                          item={{ value: plan.code, label: plan.name }}
                        >
                          <Select.ItemText>{plan.name}</Select.ItemText>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
                {assistsError && (
                  <div className='mt-2 flex items-center gap-3'>
                    <Text className='text-red-500 text-sm'>
                      Erro ao carregar assistências
                    </Text>
                    <Button
                      variant='secondary'
                      onClick={() => refetchAssists()}
                    >
                      Tentar novamente
                    </Button>
                  </div>
                )}
                {showValidationErrors && !assistanceChoice && (
                  <Text className='text-red-500 text-sm mt-2'>
                    Selecione um pacote de assistência para avançar.
                  </Text>
                )}
              </Field.Root>

              {assistanceChoice && (
                <div className='mt-4 flex flex-col gap-4 w-full'>
                  {isLoadingPlanResources && (
                    <Text className='text-sm text-alba-gray-600'>
                      Carregando recursos do plano selecionado...
                    </Text>
                  )}
                  {planResourcesError && (
                    <div className='flex items-center gap-3'>
                      <Text className='text-red-500 text-sm'>
                        Erro ao carregar recursos do plano
                      </Text>
                      <Button
                        variant='secondary'
                        onClick={() => refetchPlanResources()}
                      >
                        Tentar novamente
                      </Button>
                    </div>
                  )}
                  {!!planResources &&
                    !isLoadingPlanResources &&
                    !planResourcesError &&
                    Array.isArray(planResources) && (
                      <div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2'>
                        {planResources
                          .filter(resource => {
                            // Oculta o item "guarda-chuva" ASSIST (duplica o
                            // título da seção) e o CAPITA — só exibição; o
                            // payload é montado à parte em parsedBenefits.
                            const code = String(resource.code || '')
                              .trim()
                              .toUpperCase();
                            return code !== 'CAPITA' && code !== 'ASSIST';
                          })
                          .map(resource => (
                            <AssistancePackageCard
                              key={resource.code}
                              title={resource.name}
                              description={resource.description}
                            />
                          ))}
                      </div>
                    )}
                </div>
              )}
            </div>
            </RequiredFieldTooltip>
          </div>
        </div>

        <div className='flex flex-row items-center justify-between gap-3 self-stretch px-4 sm:gap-6 sm:px-6'>
          <Button
            variant='ghost'
            size='md'
            className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 sm:h-auto'
            onClick={onBack}
          >
            <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
            Voltar
          </Button>
          <Button
            variant='primary'
            size='md'
            className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
            onClick={handleSubmit}
            disabled={isPending}
          >
            Próximo
            <ArrowRight className='w-6 h-6' strokeWidth={2} />
          </Button>
        </div>
      </div>
    );
  },
});
